// Fixed mappings & formatting helpers (see PRD sections 7 & 8)

export const CITIES = [
  "Москва", "Московская область", "Санкт-Петербург", "Ленинградская область",
  "Новосибирск", "Екатеринбург", "Казань", "Нижний Новгород", "Челябинск",
  "Самара", "Омск", "Ростов-на-Дону", "Уфа", "Красноярск", "Воронеж",
  "Пермь", "Волгоград", "Краснодар", "Сочи",
];

export const PROPERTY_TYPES = {
  apartment: { ru: "Квартира", en: "Apartment" },
  house: { ru: "Дом", en: "House" },
  townhouse: { ru: "Таунхаус", en: "Townhouse" },
  parking: { ru: "Паркинг", en: "Parking" },
  commercial: { ru: "Коммерческая", en: "Commercial" },
};

export const LISTING_TYPES = {
  rent: { ru: "Аренда", en: "Rent" },
  "short-term": { ru: "Посуточно", en: "Daily" },
  sale: { ru: "Купить", en: "Buy" },
};

export const AMENITIES = [
  { key: "playground", ru: "Детская площадка", en: "Playground" },
  { key: "fitness", ru: "Фитнес", en: "Fitness" },
  {
    key: "sports", ru: "Спортивные площадки", en: "Sports grounds",
    children: [
      { key: "tennis", ru: "Теннис", en: "Tennis" },
      { key: "basketball", ru: "Баскетбол", en: "Basketball" },
      { key: "padel", ru: "Падел-теннис", en: "Padel tennis" },
      { key: "football", ru: "Футбол", en: "Football" },
    ],
  },
  { key: "beauty", ru: "Салон красоты", en: "Beauty salon" },
  { key: "restaurants", ru: "Рестораны", en: "Restaurants" },
  { key: "cafe", ru: "Кафе", en: "Cafe" },
  {
    key: "pickup", ru: "Пункты выдачи заказов", en: "Pickup points",
    children: [
      { key: "ozon", ru: "Ozon", en: "Ozon" },
      { key: "wildberries", ru: "Wildberries", en: "Wildberries" },
      { key: "yandexmarket", ru: "Яндекс Маркет", en: "Yandex Market" },
      { key: "cdek", ru: "СДЭК", en: "CDEK" },
    ],
  },
];

export const propertyTypeLabel = (v, lang) => PROPERTY_TYPES[v]?.[lang] || v || "";
export const listingTypeLabel = (v, lang) => LISTING_TYPES[v]?.[lang] || v || "";

// Cover image: cover.url -> first non-floorplan image. Never a floor plan.
export const coverUrl = (listing) => {
  if (listing?.cover?.url && !listing.cover.isFloorPlan) return listing.cover.url;
  const imgs = listing?.images || [];
  const first = imgs.find((i) => !i.isFloorPlan);
  return first?.url || imgs[0]?.url || null;
};

export const galleryImages = (listing) =>
  (listing?.images || []).filter((i) => !i.isFloorPlan);

export const floorPlans = (listing) =>
  (listing?.images || []).filter((i) => i.isFloorPlan);

export const isVerifiedCover = (listing) =>
  listing?.cover?.verified === true ||
  listing?.location?.verification === "matched" ||
  listing?.location?.verification === "verified";

const nbsp = "\u00A0";

export const formatPrice = (price, lang, t) => {
  const amount = price?.amount;
  if (amount == null) return t("priceOnRequest");
  const formatted = new Intl.NumberFormat("ru-RU").format(amount).replace(/\s/g, nbsp);
  const symbol = price?.currency === "RUB" || !price?.currency ? "₽" : price.currency;
  let suffix = "";
  if (price?.period === "month") suffix = ` ${t("perMonth")}`;
  else if (price?.period === "day") suffix = ` ${t("perDay")}`;
  return `${formatted}${nbsp}${symbol}${suffix}`;
};

export const roomsLabel = (rooms, t) => {
  if (rooms === 0 || rooms == null) return t("studio");
  return `${rooms}`;
};

export const formatDate = (iso, lang) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(lang === "ru" ? "ru-RU" : "en-US", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch {
    return "";
  }
};

export const yandexMapUrl = (listing) => {
  const q = listing?.location?.address || listing?.location?.city || "";
  return `https://yandex.ru/maps/?text=${encodeURIComponent(q)}`;
};

export const photoCount = (listing) => (listing?.images || []).length;

const SLOT_RANK = { today: 0, tomorrow: 1, aftertomorrow: 2 };
export const slotRank = (listing) => {
  const s = listing?.availability?.earliestSlot;
  if (s in SLOT_RANK) return SLOT_RANK[s];
  const d = new Date(s);
  return isNaN(d) ? 999 : (d - Date.now()) / 86400000 + 3;
};

export const earliestSlotLabel = (slot, t, lang) => {
  if (!slot) return "";
  if (slot === "today") return t("earliest_today");
  if (slot === "tomorrow") return t("earliest_tomorrow");
  if (slot === "aftertomorrow") return t("earliest_aftertomorrow");
  return formatDate(slot, lang);
};

export const formatDateNumeric = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" });
};

export const availabilityDate = (listing) => {
  const a = listing?.availability || {};
  if (a.availableFrom) return a.availableFrom;
  const s = a.earliestSlot;
  const now = new Date();
  if (s === "today") return now.toISOString();
  if (s === "tomorrow") { now.setDate(now.getDate() + 1); return now.toISOString(); }
  if (s === "aftertomorrow") { now.setDate(now.getDate() + 2); return now.toISOString(); }
  if (s && !isNaN(new Date(s))) return s;
  return null;
};

export const ageInDays = (iso) => {
  if (!iso) return Infinity;
  const d = new Date(iso);
  return isNaN(d) ? Infinity : (Date.now() - d) / 86400000;
};
